import React, { useState } from 'react';
import { usePOSStore } from '../../../stores/pos.store';
import { Button } from '../../common/Button';
import { Plus, Trash2, User, Search, Package, ArrowRight, X, Sparkles, ShoppingCart, AlertTriangle } from 'lucide-react';
import { useStaff, useCustomers, usePackages, useBaseCurrency } from '../../../db/hooks/useLookupData';
import { detectMatchingCombos } from '../../../lib/posComboDetector';
import { POSOperatorPickerModal } from '../POSOperatorPickerModal';
import { POSEditCartItemView } from './POSEditCartItemView';
import { useToast } from '../../common/ToastProvider';

export function POSCartStep() {
  const {
    cartItems,
    removeCartItem,
    updateCartItem,
    setIsSelectingService,
    addPackageToCart,
    customer,
    setCustomer,
    setStep,
    discount,
    setDiscount,
    isCustomerSearchOpen,
    setIsCustomerSearchOpen,
    defaultOperatorId,
    setDefaultOperatorId,
    openStaffCreation,
    editingCartItemId,
    setEditingCartItemId,
  } = usePOSStore();
  const { staff = [] } = useStaff();
  const { customers = [] } = useCustomers();
  const { packages = [] } = usePackages();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const toast = useToast();
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const editingItem = cartItems.find((i) => i.id === editingCartItemId);

  const handleAddNewStaff = (targetItemId?: string | null) => {
    const target = targetItemId || editingCartItemId || 'default';
    setIsPickerOpen(false);
    setEditingCartItemId(null);
    openStaffCreation(target);
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) || 
    (c.email && c.email.toLowerCase().includes(clientSearchQuery.toLowerCase())) ||
    (c.phone && c.phone.includes(clientSearchQuery))
  );

  // Evaluación inteligente de combos en tiempo real
  const comboMatch = detectMatchingCombos(cartItems, packages);

  const handleApplyCombo = () => {
    if (!comboMatch) return;
    comboMatch.matchingItemIds.forEach(id => removeCartItem(id));
    addPackageToCart(comboMatch.packageDoc, null);
  };

  const subtotal = cartItems.reduce((acc, item) => acc + Math.max(0, item.unitPrice - (item.discountAmount || 0)) * item.quantity, 0);

  const handleContinueToPayment = () => {
    if (cartItems.length === 0) return;

    // 1. Validar que todos los services tengan operador asignado
    const unassignedItem = cartItems.find((i) => !i.operatorId);
    if (unassignedItem) {
      // Auto-assign if only 1 staff or prompt picker
      if (staff.length === 1) {
        setDefaultOperatorId(staff[0].id);
      } else {
        const serviceName = unassignedItem.service?.concept || unassignedItem.package?.name || 'servicio';
        toast.error(
          `Debes seleccionar un profesional/esteticista para "${serviceName}" antes de continuar.`,
          'Operador Requerido'
        );
        setIsPickerOpen(true);
        return;
      }
    }

    // 2. Validar que la compra de packages o volumen tenga un cliente asociado
    const hasPackages = cartItems.some((i) => i.type === 'package' || Boolean(i.package) || Boolean(i.selectedTier));
    if (hasPackages && !customer) {
      toast.error(
        'La compra de packages o combos promocionales requiere asociar un cliente para acreditar sus sesiones.',
        'Cliente Requerido'
      );
      setIsCustomerSearchOpen(true);
      return;
    }

    setStep('tips');
  };

  // If user is editing a specific cart item, render the Fresha-style subview
  if (editingItem) {
    return (
      <POSEditCartItemView
        item={editingItem}
        onBack={() => setEditingCartItemId(null)}
        onSave={(updates) => updateCartItem(editingItem.id, updates)}
        onDelete={() => {
          removeCartItem(editingItem.id);
          setEditingCartItemId(null);
        }}
        onAddNewStaff={() => handleAddNewStaff(editingItem.id)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full animate-fade-in relative overflow-hidden bg-surface">
      
      {/* ── Scrollable Content ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-2.5 sm:p-6 flex flex-col gap-2.5 sm:gap-4">
        
        {/* Smart Combo Banner */}
        {comboMatch && (
          <div className="p-3 sm:p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center justify-between gap-2.5 sm:gap-3 animate-fade-in shadow-xs">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 shrink-0" />
              <div>
                <h5 className="font-extrabold text-xs text-text-primary">
                  ¡Combo Detectado! "{comboMatch.packageDoc.name}"
                </h5>
                <p className="text-[11px] text-text-secondary">
                  Ahorras <span className="font-bold text-amber-500">{currencySymbol}{comboMatch.savings.toFixed(2)} {baseCurrency}</span> vs selección individual.
                </p>
              </div>
            </div>
            <Button size="xs" variant="primary" onClick={handleApplyCombo} className="bg-amber-500 hover:bg-amber-600 border-none text-white font-extrabold shrink-0">
              Aplicar Combo
            </Button>
          </div>
        )}

        {/* Cart Items List Container — Unnested on mobile (<1024px) for full-width card readability */}
        <div className="sm:bg-surface-secondary sm:backdrop-blur-md sm:border sm:border-border p-0 sm:p-5 rounded-2xl sm:shadow-xs flex flex-col min-h-0">
          
          {/* Header */}
          <div className="flex items-center justify-between gap-1.5 mb-2.5 sm:mb-4 pb-2 sm:pb-3 border-b border-border">
            <h4 className="text-xs sm:text-sm font-extrabold text-text-primary min-w-0 truncate">
              <span className="inline sm:hidden">Carrito ({cartItems.length})</span>
              <span className="hidden sm:inline">Servicios & Combos en Carrito ({cartItems.length})</span>
            </h4>

            {/* Mobile Client Chip (< 1024px) */}
            <button
              type="button"
              onClick={() => setIsCustomerSearchOpen(true)}
              className="lg:hidden flex items-center gap-1 bg-brand-orange/10 border border-brand-orange/30 hover:bg-brand-orange/20 px-2 py-0.5 rounded-xl text-[11px] font-bold text-brand-orange transition-colors cursor-pointer shrink-0"
            >
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[110px] sm:max-w-[130px]">{customer ? customer.name : 'Asociar Cliente'}</span>
            </button>
          </div>

          {cartItems.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-4 sm:p-6 border-2 border-dashed border-border rounded-xl min-h-[120px]">
              <ShoppingCart className="w-8 h-8 sm:w-10 sm:h-10 mb-2 opacity-20 text-text-tertiary" />
              <p className="text-sm sm:text-base font-bold text-text-secondary">El carrito está vacío</p>
              <p className="text-[11px] sm:text-xs mt-0.5 text-text-tertiary max-w-xs">Añade services o packages para iniciar la venta</p>
            </div>
          ) : (
            <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar pr-1">
              {cartItems.map((item) => {
                const isPackage = item.type === 'package' || Boolean(item.package);
                const title = isPackage ? item.package?.name : item.service?.concept;
                const commPercent = item.service?.commission_percentage || 0;
                const operator = staff.find(p => p.id === item.operatorId);

                return (
                  <div key={item.id} className="bg-surface border border-border p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-xs flex justify-between items-start border-l-4 border-l-brand-sky transition-all">
                    <div className="flex-1 pr-2 sm:pr-4 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {isPackage && (
                            <span className="bg-brand-sky/10 text-brand-sky border border-brand-sky/20 text-[10px] font-extrabold px-1.5 py-0.5 rounded">
                              COMBO
                            </span>
                          )}
                          <h5 className="font-bold text-xs sm:text-sm text-text-primary">{title || 'Ítem'}</h5>
                        </div>
                        <span className="font-extrabold text-xs sm:text-sm text-text-primary">
                          {currencySymbol}{(item.unitPrice * item.quantity).toFixed(2)}
                        </span>
                      </div>

                      {/* Fresha-style staff detail & item discount line */}
                      <div className="text-xs text-text-secondary mt-1 flex flex-wrap items-center gap-2">
                        {item.selectedTier ? (
                          <span className="font-bold text-brand-sky bg-brand-sky/10 px-2 py-0.5 rounded-full border border-brand-sky/20 text-[11px]">
                            {item.selectedTier.label} ({item.selectedTier.quantity} sesiones incluidas)
                          </span>
                        ) : (
                          <span>{isPackage ? 'Paquete promocional' : `${item.service?.duration_minutes || 30} min`}</span>
                        )}
                        <span>•</span>
                        
                        {/* Staff badge + change trigger */}
                        <div className="relative inline-flex items-center gap-1">
                          <span className={`font-bold ${operator ? 'text-text-primary' : 'text-amber-500'}`}>
                            {operator ? operator.name : (
                              <span className="inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3 text-amber-500" />
                                Sin esteticista
                              </span>
                            )}
                          </span>
                          
                          <button
                            type="button"
                            onClick={() => setEditingCartItemId(item.id)}
                            className="text-[11px] font-bold text-brand-sky hover:underline ml-1 cursor-pointer"
                          >
                            (editar)
                          </button>
                        </div>

                        {item.discountAmount && item.discountAmount > 0 ? (
                          <span className="text-[10px] font-extrabold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                            Desc: -{currencySymbol}{item.discountAmount.toFixed(2)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => removeCartItem(item.id)}
                      className="text-text-tertiary hover:text-error transition-colors p-1 cursor-pointer"
                      title="Eliminar del carrito"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Action Button (Single Clean CTA) */}
          <div className="mt-3 sm:mt-4 pt-2.5 sm:pt-3 border-t border-border">
            <Button 
              data-testid="add-service-btn"
              variant="outline" 
              icon={Plus}
              fullWidth
              className="border-dashed hover:border-brand-orange hover:text-brand-orange transition-colors py-2.5 text-xs font-extrabold cursor-pointer"
              onClick={() => setIsSelectingService(true)}
            >
              Añadir
            </Button>
          </div>
        </div>

      </div>

      {/* Sticky Bottom Actions Bar */}
      <div className="p-4 px-6 bg-surface-secondary border-t border-border hidden lg:flex items-center justify-between gap-4 shrink-0 shadow-lg">
        <div>
          <span className="text-xs text-text-secondary block font-semibold">Total a cobrar</span>
          <span className="text-xl font-black text-brand-orange">
            {currencySymbol}{Math.max(0, subtotal - discount).toFixed(2)} {baseCurrency}
          </span>
        </div>

        <Button 
          variant="primary" 
          size="lg" 
          icon={ArrowRight} 
          iconPosition="right"
          onClick={handleContinueToPayment}
          disabled={cartItems.length === 0}
          className="bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold border-none shadow-lg shadow-brand-orange/20"
          data-testid="pos-cart-continuar-desktop"
        >
          Continuar ({cartItems.length} ítem{cartItems.length !== 1 ? 's' : ''})
        </Button>
      </div>

      {/* Global Operator Selector Modal */}
      <POSOperatorPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelectOperator={(opId) => setDefaultOperatorId(opId)}
        onAddNewStaff={() => handleAddNewStaff('default')}
      />

      {/* Client Search Modal */}
      {isCustomerSearchOpen && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-surface border border-border rounded-3xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="font-extrabold text-base text-text-primary">Asociar Cliente a la Venta</h3>
              <button onClick={() => setIsCustomerSearchOpen(false)} className="p-1 text-text-tertiary hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                value={clientSearchQuery}
                onChange={(e) => setClientSearchQuery(e.target.value)}
                placeholder="Buscar cliente por name, email o teléfono..."
                className="w-full bg-surface-secondary border border-border rounded-xl py-2.5 pl-10 pr-4 text-xs font-bold text-text-primary focus:border-brand-sky outline-none"
                autoFocus
              />
            </div>

            <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-2">
              {filteredCustomers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setCustomer(c);
                    setIsCustomerSearchOpen(false);
                  }}
                  className="w-full p-3 rounded-xl bg-surface-secondary/50 hover:bg-brand-sky/10 border border-border hover:border-brand-sky/30 flex items-center justify-between text-left transition-colors cursor-pointer"
                >
                  <div>
                    <span className="font-bold text-xs text-text-primary block">{c.name}</span>
                    <span className="text-[11px] text-text-secondary">{c.email || c.phone || 'Sin datos de contacto'}</span>
                  </div>
                  <User className="w-4 h-4 text-brand-sky" />
                </button>
              ))}
              {filteredCustomers.length === 0 && (
                <p className="text-center text-xs text-text-tertiary py-4 font-semibold">No se encontraron customers</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
